<template>
  <div id="AccountCustomData" class="Main-Content">
    <ValidationObserver v-slot="{ invalid }" slim>
      <LoadingLayout v-if="loading" :page="true" />

      <Page
        v-else
        :title="$t('Custom Data')"
        :primaryAction="{
          label: $t('AccountCustomData.Add Custom Data'),
          onAction: addCustomData,
          disabled: saving
        }"
        :secondaryActions="[{
          label: $t('Notes'),
          onAction: () => eventHub.$emit('showAccountNotes', 'CUSTOMDATA')
        }]"
      >

        <Layout>
          <LayoutSection>
            <Sheet
              :title="$t('AccountCustomData.Edit Custom Data')"
              :width="700"
              :visible="!!editItem"
              @close="cancelEdit"
              padded
            >
              <template v-slot:actions>
                <Button type="primary" :disabled="!canUpdate || invalid || saving" @click="updateItem">{{$t('Update')}}</Button>
              </template>
              <Card
                v-if="editItem"
              >
                <CardSection>
                  <FormLayout>
                    <validation-provider :name="$t('Field')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('Field')"
                        v-model="editItemAccess.field"
                        :placeholder="$t('Field')"
                        :disabled="currentAction === 'update'"
                        :error="errors[0]"
                      />
                    </validation-provider>
                    <Select
                      :label="$t('Type')"
                      v-model="editItemAccess.dataType"
                      :options="typeList"
                    />
                    <TextField
                      :label="$t('Label')"
                      :placeholder="$t('Label')"
                      v-model="editItemAccess.label"
                    />
                    <TextField
                      :label="$t('Description')"
                      :placeholder="$t('Description')"
                      v-model="editItemAccess.description"
                    />
                  </FormLayout>
                </CardSection>
              </Card>
            </Sheet>
            <Card>
              <Filters
                :query.sync="filter"
              />
              <DataTable
                :columns="[
                  {
                    key: 'field',
                    label: $t('Field'),
                    fixed: true
                  },
                  {
                    key: 'type',
                    label: $t('Type')
                  },
                  {
                    key: 'label',
                    label: $t('Label')
                  },
                  {
                    key: 'description',
                    label: $t('Description')
                  }
                ]"
                :rows="formattedCustomData"
                :pagination="{
                  offset: offset,
                  limit: limit,
                  total: formattedCustomData.length,
                  updateOffset: val => offset = val,
                  updateLimit: updateLimit
                }"
              >
                <template v-slot:col:field="{row}">
                  <PageLink @click="editCustomData(row)">{{row.field}}</PageLink>
                </template>
              </DataTable>
            </Card>
          </LayoutSection>
        </Layout>
      </Page>
    </ValidationObserver>
  </div>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'AccountCustomData',
  data () {
    return {
      loading: true,
      saving: false,
      customData: null,
      currentAction: '',
      editItem: null,
      editItemCopy: null,
      offset: 0,
      filter: null,
      limit: 10
    }
  },
  mounted() {
    NProgress.start()
    this.limit = this.getTableLimit('tableLimit') || 10
    this.api.getCustomData()
      .then((data) => {
        // console.log(data)
        this.customData = data
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
  },
  computed: {
    typeLabelMap() {
      return {
        string: this.$t('AccountCustomData.String'),
        date: this.$t('Date'),
        bool: this.$t('AccountCustomData.Boolean'),
        integer: this.$t('AccountCustomData.Integer'),
        integerId: this.$t('AccountCustomData.integerUnformatted'),
        float: this.$t('AccountCustomData.Float'),
        decimal: this.$t('AccountCustomData.Decimal')
      }
    },
    editItemAccess() {
      return this.editItem || {}
    },
    typeList() {
      const rv = []
      for (const k of Object.keys(this.typeLabelMap)) {
        rv.push({
          label: this.typeLabelMap[k],
          value: k
        })
      }
      return rv
    },
    formattedCustomData() {
      let CD = this.customData.map(cd => {
        return {
          ...cd,
          type: this.typeLabelMap[cd.dataType]
        }
      })
      if (this.filter) {
        CD = CD.filter(row => JSON.stringify(row).toLowerCase().indexOf(this.filter) >= 0)
      }
      return CD
    },
    canUpdate() {
      return this.editItem && this.editItem.field && this.editItem.dataType
    }
  },
  methods: {
    async cancelEdit() {
      if (!_.isEqual(this.editItem, this.editItemCopy)) {
        const confirm = await this.$ui.confirm({
          title: this.$t('Unsaved Changes'),
          content: this.$t('AccountCustomData.There are unsaved changes in this custom data'),
          primaryActionLabel: this.$t('Lose changes and return to list'),
          primaryActionType: 'destructive',
          secondaryActionLabel: this.$t('Continue editing')
        })
        if (!confirm) {
          return
        }
      }
      this.currentAction = ''
      this.editItem = null
      this.editItemCopy = null
    },
    editCustomData(item) {
      this.currentAction = 'update'
      this.editItem = {
        field: item.field,
        dataType: item.dataType,
        label: item.label,
        description: item.description
      }
      this.editItemCopy = _.cloneDeep(this.editItem)
    },
    addCustomData() {
      this.currentAction = 'add'
      this.editItem = {
        field: null,
        dataType: 'string',
        label: null,
        description: null
      }
      this.editItemCopy = _.cloneDeep(this.editItem)
    },
    updateLimit(limit) {
      this.setTableLimit('tableLimit', limit)
      this.limit = limit
      this.offset = 0
    },
    updateItem() {
      if (!this.editItem.field) { return; }
      // need to check for correct lengths but i dont really care about that now
      if (this.currentAction === 'update') {
        NProgress.start()
        this.saving = true
        this.api.updateCustomData(this.editItem)
          .then(() => {
            const ind = this.customData.findIndex(cd => cd.field === this.editItem.field)
            this.$set(this.customData, ind, this.editItem)
            this.editItem = null
            this.editItemCopy = null
            this.currentAction = ''
            this.$ui.toast({ title: this.$t('AccountCustomData.Custom Data Updated') })
          })
          .catch(this.apiCatch)
          .finally(() => {
            this.saving = false
            NProgress.done()
          })
      } else if (this.currentAction === 'add') {
        NProgress.start()
        this.saving = true
        this.api.createCustomData(this.editItem)
          .then(() => {
            this.customData.push(this.editItem)
            this.editItem = null
            this.editItemCopy = null
            this.currentAction = ''
            this.$ui.toast({ title: this.$t('AccountCustomData.Custom Data Created') })
          })
          .catch(this.apiCatch)
          .finally(() => {
            this.saving = false
            NProgress.done()
          })
      }
    }
  }
}

</script>